import type React from 'react';
import type { FormInstance } from 'antd';
import type { BookDefaultStylesV1, ContentAlign } from './bookDefaults';
import { mergeBookDefaultStyles, parseContentAlign } from './bookDefaults';
import { linePositionStyle, PREVIEW_TEXT_CONTAINMENT, resolveBlockIndentSpacing, resolveTextAlignCss, buildAlignChromeWidthStyle } from './bookStyleCss';
import { applyBorderValueToSides, borderSidesToFormValue, formBorderSidesToStored, normalizeBorderSides } from './borderSides';
import {
  DEFAULT_FONT_FAMILY,
  getBaseFontFamilyToken,
  isBoldFromFontFamily,
  isItalicFromFontFamily,
  resolveFontFamily,
  resolveStoredTypographyFromForm,
} from './fontFamily';
import { cleanTagLineStyles } from './tagLineStyles';

type StoredStyleLike = Record<string, unknown> | undefined;

export const DEFAULT_STYLE_ALIGN_FIELD_PREFIXES = [
  'defaultHeaderStyle',
  'defaultLineStyle',
  'defaultNestedLineStyle',
  'defaultDeepNestedLineStyle',
  'defaultLinkStyle',
] as const;

export function normalizeFormContentAlign(value: unknown): ContentAlign {
  return parseContentAlign(value, 'left');
}

function resolveFormAlign(style: StoredStyleLike, fallbackAlign: ContentAlign): ContentAlign {
  const align = style?.align;
  if (
    align === 'left' ||
    align === 'center' ||
    align === 'right' ||
    align === 'justify' ||
    align === 'justify-center'
  ) {
    return align;
  }
  const textAlign = style?.textAlign;
  if (
    textAlign === 'left' ||
    textAlign === 'center' ||
    textAlign === 'right' ||
    textAlign === 'justify' ||
    textAlign === 'justify-center'
  ) {
    return textAlign;
  }
  return fallbackAlign;
}

function lineHeightFormValue(value: unknown, fallback = 1): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function storedTypographyStyleToForm(
  style: StoredStyleLike,
  opts: { defaultBoldWeight?: number; fallbackAlign: ContentAlign },
) {
  const src = style || {};
  const {
    textAlign: _textAlign,
    align: _align,
    bold: _bold,
    italic: _italic,
    underline: _underline,
    lineHeight,
    fontWeight,
    fontStyle,
    textDecoration,
    borderSide: _borderSide,
    borderSides: _borderSides,
    ...rest
  } = src;
  const weight = fontWeight ?? opts.defaultBoldWeight ?? 400;
  const fontFamily = typeof src.fontFamily === 'string' ? src.fontFamily : undefined;
  return {
    ...rest,
    fontFamily: fontFamily ? getBaseFontFamilyToken(fontFamily) ?? fontFamily : DEFAULT_FONT_FAMILY,
    bold: isBoldFromFontFamily(fontFamily) ?? Number(weight) >= 600,
    italic: isItalicFromFontFamily(fontFamily) ?? fontStyle === 'italic',
    underline: String(textDecoration || '').includes('underline'),
    align: resolveFormAlign(src, opts.fallbackAlign),
    lineHeight: lineHeightFormValue(lineHeight),
    borderSides: borderSidesToFormValue(src),
  };
}

function storedLinkStyleToForm(style: StoredStyleLike, fallbackAlign: ContentAlign) {
  const src = style || {};
  const { textAlign: _textAlign, align: _align, ...rest } = src;
  return {
    ...rest,
    align: resolveFormAlign(src, fallbackAlign),
  };
}

export function buildContentAlignCascadePatch(
  current: BookDefaultStylesFormValues,
  contentAlign: unknown,
): Partial<BookDefaultStylesFormValues> {
  const align = normalizeFormContentAlign(contentAlign);
  const patch: Partial<BookDefaultStylesFormValues> = {};
  for (const prefix of DEFAULT_STYLE_ALIGN_FIELD_PREFIXES) {
    const style = current[prefix];
    patch[prefix] = {
      ...(style && typeof style === 'object' ? style : {}),
      align,
    };
  }
  return patch;
}

/** When page layout alignment changes, replace align on every default style tab. */
export function applyContentAlignCascadeToForm(form: FormInstance, contentAlign: unknown): void {
  const current = form.getFieldsValue(true) as BookDefaultStylesFormValues;
  form.setFieldsValue(buildContentAlignCascadePatch(current, contentAlign));
}

export type { ContentAlign } from './bookDefaults';

export type BookDefaultStylesFormValues = {
  pagePaddingPx?: number;
  baseFontSizePx?: number;
  gridGapPx?: number;
  contentLineGapPx?: number;
  contentAlign?: ContentAlign;
  defaultTextColor?: string;
  defaultBackgroundColor?: string;
  defaultLinkColor?: string;
  defaultHeaderStyle?: any;
  defaultLineStyle?: any;
  defaultNestedLineStyle?: any;
  defaultDeepNestedLineStyle?: any;
  defaultLinkStyle?: any;
  tagLineStyles?: Record<string, unknown>;
  tagStyleDraft?: any;
};

export function asPreviewCss(s: any): React.CSSProperties {
  if (!s || typeof s !== 'object') return {};
  const textAlign = s.align ?? s.textAlign;
  const borderWidth = Number.isFinite(Number(s.borderWidth)) ? Number(s.borderWidth) : 0;
  const borderStyle = s.borderStyle;
  const borderColor = typeof s.borderColor === 'string' ? s.borderColor : undefined;
  const borderValue =
    borderStyle && borderStyle !== 'none' && borderWidth > 0
      ? `${borderWidth}px ${borderStyle} ${borderColor || (typeof s.color === 'string' ? s.color : '#d9d9d9')}`
      : undefined;
  const borderCss = !borderValue
    ? {}
    : (() => {
        const css: React.CSSProperties = {};
        applyBorderValueToSides(css, borderValue, normalizeBorderSides(s));
        return css;
      })();
  const indentSpacing = resolveBlockIndentSpacing(s);
  const resolvedTextAlign =
    textAlign === 'left' ||
    textAlign === 'center' ||
    textAlign === 'right' ||
    textAlign === 'justify' ||
    textAlign === 'justify-center'
      ? textAlign
      : undefined;
  const textAlignCss = resolveTextAlignCss(resolvedTextAlign);
  const typography = resolveStoredTypographyFromForm({
    fontFamily: s.fontFamily,
    bold: s.bold,
    italic: s.italic,
    fontWeight: s.fontWeight,
  });
  const resolvedFontFamily = typography.fontFamily
    ? resolveFontFamily(typography.fontFamily)
    : undefined;
  return {
    fontSize: Number.isFinite(Number(s.fontSize)) ? Number(s.fontSize) : undefined,
    color: typeof s.color === 'string' ? s.color : undefined,
    backgroundColor: typeof s.backgroundColor === 'string' ? s.backgroundColor : undefined,
    fontFamily: resolvedFontFamily,
    fontWeight: typography.fontWeight,
    fontStyle: typography.fontStyle === 'italic' ? 'italic' : undefined,
    textDecoration: s.underline ? 'underline' : undefined,
    ...textAlignCss,
    ...borderCss,
    borderRadius: Number.isFinite(Number(s.borderRadius)) ? Number(s.borderRadius) : undefined,
    padding: indentSpacing.padding,
    margin: indentSpacing.margin,
    display: 'inline-block',
    ...PREVIEW_TEXT_CONTAINMENT,
    ...buildAlignChromeWidthStyle(resolvedTextAlign),
    whiteSpace: 'pre-wrap',
    ...linePositionStyle(resolvedTextAlign),
  };
}

export function bookDefaultsToFormValues(cur: BookDefaultStylesV1): BookDefaultStylesFormValues {
  const contentAlign = cur.contentAlign ?? 'left';
  return {
    pagePaddingPx: cur.pagePaddingPx,
    baseFontSizePx: cur.baseFontSizePx,
    gridGapPx: cur.gridGapPx,
    contentLineGapPx: cur.contentLineGapPx ?? 0,
    contentAlign,
    defaultTextColor: cur.defaultTextColor || '',
    defaultBackgroundColor: cur.defaultBackgroundColor || '',
    defaultLinkColor: cur.defaultLinkColor || '',
    defaultHeaderStyle: storedTypographyStyleToForm(cur.defaultHeaderStyle, {
      defaultBoldWeight: 600,
      fallbackAlign: contentAlign,
    }),
    defaultLineStyle: storedTypographyStyleToForm(cur.defaultLineStyle, {
      defaultBoldWeight: 400,
      fallbackAlign: contentAlign,
    }),
    defaultNestedLineStyle: storedTypographyStyleToForm(cur.defaultNestedLineStyle, {
      defaultBoldWeight: 400,
      fallbackAlign: contentAlign,
    }),
    defaultDeepNestedLineStyle: storedTypographyStyleToForm(cur.defaultDeepNestedLineStyle, {
      defaultBoldWeight: 400,
      fallbackAlign: contentAlign,
    }),
    defaultLinkStyle: storedLinkStyleToForm(cur.defaultLinkStyle, contentAlign),
    tagLineStyles: cur.tagLineStyles ? { ...cur.tagLineStyles } : {},
  };
}

/** Merge live form values onto the selected template so unmounted tabs (e.g. Book defaults) are not lost on save. */
export function mergeBookDefaultsFormValues(
  formVals: BookDefaultStylesFormValues,
  base: BookDefaultStylesV1,
): BookDefaultStylesFormValues {
  const baseForm = bookDefaultsToFormValues(base);
  const mergeStyle = (baseStyle: any, patch: any) =>
    patch != null && typeof patch === 'object' ? { ...baseStyle, ...patch } : baseStyle;

  return {
    ...baseForm,
    ...formVals,
    defaultHeaderStyle: mergeStyle(baseForm.defaultHeaderStyle, formVals.defaultHeaderStyle),
    defaultLineStyle: mergeStyle(baseForm.defaultLineStyle, formVals.defaultLineStyle),
    defaultNestedLineStyle: mergeStyle(baseForm.defaultNestedLineStyle, formVals.defaultNestedLineStyle),
    defaultDeepNestedLineStyle: mergeStyle(
      baseForm.defaultDeepNestedLineStyle,
      formVals.defaultDeepNestedLineStyle,
    ),
    defaultLinkStyle: mergeStyle(baseForm.defaultLinkStyle, formVals.defaultLinkStyle),
    tagLineStyles:
      formVals.tagLineStyles != null && typeof formVals.tagLineStyles === 'object'
        ? { ...(formVals.tagLineStyles as Record<string, unknown>) }
        : baseForm.tagLineStyles,
  };
}

export function formValuesToBookDefaults(vals: any, base: BookDefaultStylesV1): BookDefaultStylesV1 {
  const cleanStringOrUndef = (v: any) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const cleanNumberOrUndef = (v: any) => (v != null && Number.isFinite(Number(v)) ? Number(v) : undefined);
  const cleanPositiveOrUndef = (v: any) => {
    const n = cleanNumberOrUndef(v);
    return n != null && n > 0 ? n : undefined;
  };
  const cleanLineHeightOrUndef = (v: any) => {
    const n = cleanNumberOrUndef(v);
    return n != null && n > 0 ? n : undefined;
  };
  const cleanStyle = (s: any, mode: 'header' | 'line') => {
    if (!s || typeof s !== 'object') return undefined;
    const align = s.align ?? s.textAlign;
    const span = s.span === 12 || s.span === 24 ? s.span : undefined;
    const typography = resolveStoredTypographyFromForm({
      fontFamily: s.fontFamily,
      bold: s.bold,
      italic: s.italic,
      fontWeight: mode === 'line' ? ('bold' in s ? (s.bold ? 700 : 400) : s.fontWeight) : s.fontWeight,
    });
    const borderSidesStored = formBorderSidesToStored(s);
    const baseStyle: any = {
      fontSize: cleanPositiveOrUndef(s.fontSize),
      color: cleanStringOrUndef(s.color),
      backgroundColor: cleanStringOrUndef(s.backgroundColor),
      fontFamily: cleanStringOrUndef(typography.fontFamily ?? s.fontFamily),
      fontWeight: cleanNumberOrUndef(typography.fontWeight),
      fontStyle: typography.fontStyle,
      textDecoration:
        'underline' in s
          ? s.underline
            ? 'underline'
            : 'none'
          : s.underline
            ? 'underline'
            : undefined,
      textAlign:
        align === 'left' ||
        align === 'center' ||
        align === 'right' ||
        align === 'justify' ||
        align === 'justify-center'
          ? align
          : undefined,
      borderWidth: cleanPositiveOrUndef(s.borderWidth),
      borderStyle:
        s.borderStyle === 'solid' || s.borderStyle === 'dashed' || s.borderStyle === 'dotted' || s.borderStyle === 'double'
          ? s.borderStyle
          : undefined,
      borderColor: cleanStringOrUndef(s.borderColor),
      ...borderSidesStored,
      borderRadius: cleanPositiveOrUndef(s.borderRadius),
      padding: cleanPositiveOrUndef(s.padding),
      paddingLeft: cleanPositiveOrUndef(s.paddingLeft),
      paddingRight: cleanPositiveOrUndef(s.paddingRight),
      paddingTop: cleanPositiveOrUndef(s.paddingTop),
      paddingBottom: cleanPositiveOrUndef(s.paddingBottom),
      margin: cleanPositiveOrUndef(s.margin),
      marginLeft: cleanPositiveOrUndef(s.marginLeft),
      marginRight: cleanPositiveOrUndef(s.marginRight),
      marginTop: cleanPositiveOrUndef(s.marginTop),
      marginBottom: cleanPositiveOrUndef(s.marginBottom),
      lineHeight: cleanLineHeightOrUndef(s.lineHeight),
    };
    if (mode === 'line' && span) baseStyle.span = span;
    return baseStyle;
  };

  const numOr = (v: any, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const strOr = (v: any, fallback: string) => (typeof v === 'string' ? v : fallback);

  const mergeCleanedStyle = (
    baseStyle: Record<string, unknown> | undefined,
    formStyle: unknown,
    mode: 'header' | 'line',
  ) => {
    if (formStyle == null) return baseStyle;
    const cleaned = cleanStyle(formStyle, mode);
    if (!cleaned || Object.keys(cleaned).length === 0) return baseStyle;
    if (!baseStyle) return cleaned;
    const merged = { ...baseStyle };
    for (const [key, value] of Object.entries(cleaned)) {
      if (value !== undefined) {
        merged[key] = value;
      }
    }
    if (formStyle != null && typeof formStyle === 'object' && ('borderSides' in formStyle || 'borderSide' in formStyle)) {
      delete merged.borderSide;
      delete merged.borderSides;
      Object.assign(merged, formBorderSidesToStored(formStyle as Record<string, unknown>));
    }
    if (mode === 'line' && 'span' in (formStyle as object)) {
      const formSpan = (formStyle as { span?: unknown }).span;
      if (formSpan !== 12 && formSpan !== 24) {
        delete merged.span;
      }
    }
    if (formStyle != null && typeof formStyle === 'object') {
      const bw = (formStyle as { borderWidth?: unknown }).borderWidth;
      const bs = (formStyle as { borderStyle?: unknown }).borderStyle;
      if (bw === 0 || bw === '0' || bs === 'none') {
        delete merged.borderWidth;
        delete merged.borderStyle;
        delete merged.borderColor;
        delete (merged as any).borderSide;
        delete (merged as any).borderSides;
      }
      for (const key of ['color', 'backgroundColor', 'borderColor', 'fontFamily'] as const) {
        if (key in formStyle) {
          const value = (formStyle as Record<string, unknown>)[key];
          if (value == null || (typeof value === 'string' && !value.trim())) {
            delete merged[key];
          }
        }
      }
    }
    return merged;
  };

  return {
    version: 1 as const,
    pagePaddingPx: numOr(vals.pagePaddingPx, base.pagePaddingPx),
    baseFontSizePx: numOr(vals.baseFontSizePx, base.baseFontSizePx),
    gridGapPx: numOr(vals.gridGapPx, base.gridGapPx),
    contentLineGapPx: Math.max(0, numOr(vals.contentLineGapPx, base.contentLineGapPx ?? 0)),
    contentAlign: parseContentAlign(vals.contentAlign, parseContentAlign(base.contentAlign, 'left')),
    // Not editable in UI today; preserve.
    headerBackgroundColor: base.headerBackgroundColor || '',
    headerTextColor: base.headerTextColor || '',
    titleColor: base.titleColor || '',
    defaultTextColor: strOr(vals.defaultTextColor, base.defaultTextColor || ''),
    defaultBackgroundColor: strOr(vals.defaultBackgroundColor, base.defaultBackgroundColor || ''),
    defaultLinkColor: strOr(vals.defaultLinkColor, base.defaultLinkColor || ''),
    defaultHeaderStyle: mergeCleanedStyle(
      base.defaultHeaderStyle as Record<string, unknown> | undefined,
      vals.defaultHeaderStyle,
      'header',
    ) as BookDefaultStylesV1['defaultHeaderStyle'],
    defaultLineStyle: mergeCleanedStyle(
      base.defaultLineStyle as Record<string, unknown> | undefined,
      vals.defaultLineStyle,
      'line',
    ) as BookDefaultStylesV1['defaultLineStyle'],
    defaultNestedLineStyle: mergeCleanedStyle(
      base.defaultNestedLineStyle as Record<string, unknown> | undefined,
      vals.defaultNestedLineStyle,
      'line',
    ) as BookDefaultStylesV1['defaultNestedLineStyle'],
    defaultDeepNestedLineStyle: mergeCleanedStyle(
      base.defaultDeepNestedLineStyle as Record<string, unknown> | undefined,
      vals.defaultDeepNestedLineStyle,
      'line',
    ) as BookDefaultStylesV1['defaultDeepNestedLineStyle'],
    // Not editable in UI today; preserve.
    defaultCardHeaderStyle: base.defaultCardHeaderStyle,
    defaultCardTitleStyle: base.defaultCardTitleStyle,
    defaultTagStyle: base.defaultTagStyle,
    defaultLinkStyle: mergeCleanedStyle(
      base.defaultLinkStyle as Record<string, unknown> | undefined,
      vals.defaultLinkStyle,
      'header',
    ) as BookDefaultStylesV1['defaultLinkStyle'],
    defaultGranthaReferenceStyle: base.defaultGranthaReferenceStyle,
    defaultSankalpaBlockStyle: base.defaultSankalpaBlockStyle,
    defaultBhedStyle: base.defaultBhedStyle,
    gridColumns: vals.gridColumns === 2 ? 2 : (base.gridColumns ?? 1),
    tagLineStyles:
      vals.tagLineStyles != null && typeof vals.tagLineStyles === 'object'
        ? cleanTagLineStyles(vals.tagLineStyles)
        : cleanTagLineStyles(vals.tagLineStyles) ?? base.tagLineStyles,
    ...(base.bookTemplateAssignments && typeof base.bookTemplateAssignments === 'object'
      ? { bookTemplateAssignments: { ...base.bookTemplateAssignments } }
      : {}),
    ...(base.bookOverrides && typeof base.bookOverrides === 'object'
      ? { bookOverrides: { ...base.bookOverrides } }
      : {}),
  };
}

export type StyleTemplatePayload = BookDefaultStylesV1 & {
  bookOverrides?: Record<string, unknown>;
  bookTemplateAssignments?: Record<string, number>;
};

function cloneBookOverrides(source: StyleTemplatePayload | BookDefaultStylesV1 | undefined) {
  if (!source?.bookOverrides || typeof source.bookOverrides !== 'object') return undefined;
  return { ...(source.bookOverrides as Record<string, unknown>) };
}

/** Build API template styles from form draft while preserving per-book overrides. */
export function buildStyleTemplatePayload(
  formVals: BookDefaultStylesFormValues,
  sourceStyles: StyleTemplatePayload,
  options?: {
    bookOverridePatches?: Record<string, Record<string, unknown>>;
    saveAsNew?: boolean;
  },
): StyleTemplatePayload {
  const merged = mergeBookDefaultsFormValues(formVals, sourceStyles);
  const draft = formValuesToBookDefaults(merged, sourceStyles);

  if (options?.saveAsNew) {
    const { bookOverrides: _removed, bookTemplateAssignments: _assignments, ...templateOnly } = draft;
    return templateOnly;
  }

  const bookOverrides: Record<string, unknown> = { ...(cloneBookOverrides(sourceStyles) ?? {}) };

  if (options?.bookOverridePatches) {
    for (const [bookKey, patch] of Object.entries(options.bookOverridePatches)) {
      const existing =
        bookOverrides[bookKey] && typeof bookOverrides[bookKey] === 'object'
          ? (bookOverrides[bookKey] as Record<string, unknown>)
          : {};
      bookOverrides[bookKey] = { ...existing, ...patch };
    }
  }

  if (Object.keys(bookOverrides).length === 0) return draft;
  return { ...draft, bookOverrides };
}

/** Factory-reset template styles (optionally keeping per-book overrides when editing an existing template). */
export function createClearedBookDefaultsTemplate(options?: {
  preserveBookOverrides?: Record<string, unknown>;
  preserveBookTemplateAssignments?: Record<string, number>;
}): StyleTemplatePayload {
  const cleared = mergeBookDefaultStyles(null);
  const overrides = options?.preserveBookOverrides;
  const assignments = options?.preserveBookTemplateAssignments;
  return {
    ...cleared,
    ...(overrides && typeof overrides === 'object' && Object.keys(overrides).length > 0
      ? { bookOverrides: { ...overrides } }
      : {}),
    ...(assignments && typeof assignments === 'object' && Object.keys(assignments).length > 0
      ? { bookTemplateAssignments: { ...assignments } }
      : {}),
  };
}

/** Merge base used when building a save payload; new templates start from factory defaults, not the selected template. */
export function resolveStyleTemplateSaveBase(options: {
  saveAsNew: boolean;
  selectedStyleTemplateBase: StyleTemplatePayload;
}): StyleTemplatePayload {
  if (options.saveAsNew) {
    return mergeBookDefaultStyles(null);
  }
  return options.selectedStyleTemplateBase;
}

export function buildClearedBookDefaultsEditorState(options: {
  templateName?: string;
  saveAsNew?: boolean;
  preserveBookOverrides?: Record<string, unknown>;
  preserveBookTemplateAssignments?: Record<string, number>;
}): { clearedTemplate: StyleTemplatePayload; formValues: Record<string, unknown> } {
  const saveAsNew = !!options.saveAsNew;
  const preserveOverrides = saveAsNew ? undefined : options.preserveBookOverrides;
  const preserveAssignments = saveAsNew ? undefined : options.preserveBookTemplateAssignments;
  let clearedTemplate = createClearedBookDefaultsTemplate({
    ...(preserveOverrides && Object.keys(preserveOverrides).length > 0
      ? { preserveBookOverrides: preserveOverrides }
      : {}),
    ...(preserveAssignments && Object.keys(preserveAssignments).length > 0
      ? { preserveBookTemplateAssignments: preserveAssignments }
      : {}),
  });
  if (saveAsNew) {
    const {
      bookOverrides: _removed,
      tagLineStyles: _tags,
      bookTemplateAssignments: _assignments,
      ...factoryTemplate
    } = clearedTemplate;
    clearedTemplate = factoryTemplate;
  }
  return {
    clearedTemplate,
    formValues: {
      ...bookDefaultsToFormValues(clearedTemplate),
      templateName: options.templateName ?? '',
      saveAsNew,
      tagLineStyles: {},
      tagStyleDraft: {},
    },
  };
}

/** When “Save as new” is enabled, drop inherited template tags/overrides from the in-memory draft. */
export function stripTemplateDraftForSaveAsNew(options: {
  form: { getFieldValue: (name: string) => unknown; setFieldsValue: (values: Record<string, unknown>) => void };
  setSelectedStyleTemplateBase: (base: StyleTemplatePayload) => void;
  preserveTemplateName?: boolean;
}): void {
  const fresh = mergeBookDefaultStyles(null);
  const templateName = options.preserveTemplateName
    ? String(options.form.getFieldValue('templateName') ?? '')
    : '';
  options.setSelectedStyleTemplateBase(fresh);
  options.form.setFieldsValue({
    tagLineStyles: {},
    tagStyleDraft: {},
    saveAsNew: true,
    ...(templateName ? { templateName } : {}),
  });
}

/** Apply a full template reset to the editor form and in-memory draft. */
export function applyClearedBookDefaultsEditorState(options: {
  form: {
    getFieldValue: (name: string) => unknown;
    resetFields: () => void;
    setFieldsValue: (values: Record<string, unknown>) => void;
  };
  setSelectedStyleTemplateBase: (base: StyleTemplatePayload) => void;
  templateName?: string;
  saveAsNew?: boolean;
  preserveBookOverrides?: Record<string, unknown>;
  preserveBookTemplateAssignments?: Record<string, number>;
}): StyleTemplatePayload {
  const { clearedTemplate, formValues } = buildClearedBookDefaultsEditorState({
    templateName: options.templateName ?? String(options.form.getFieldValue('templateName') ?? ''),
    saveAsNew: options.saveAsNew ?? !!options.form.getFieldValue('saveAsNew'),
    preserveBookOverrides: options.preserveBookOverrides,
    preserveBookTemplateAssignments: options.preserveBookTemplateAssignments,
  });
  options.setSelectedStyleTemplateBase(clearedTemplate);
  options.form.resetFields();
  options.form.setFieldsValue(formValues);
  return clearedTemplate;
}

